import { memo, useCallback, useState } from 'react';

import { WorkspaceAgentFilterList } from '~/components/v2/components/workspace/workspace-agent-panel/components/consent/form/WorkspaceAgentFilterList';
import { WorkspaceAgentPanelConsentDock } from '~/components/v2/components/workspace/workspace-agent-panel/components/consent/WorkspaceAgentPanelConsentDock';
import { PrivateWorkspaceAgentMonikerText } from '~/components/v2/components/workspace/workspace-agent-panel/composer-host/read/PrivateWorkspaceAgentMonikerText';
import { useTrackWorkspaceAgentFormChoice } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useTrackWorkspaceAgentFormChoice';
import { usePrivateWorkspaceAgentPanelSession } from '~/components/v2/components/workspace/workspace-agent-panel/PrivateWorkspaceAgentPanelProvider';
import { type SearchSelectElicitation } from '~/components/v2/components/workspace/workspace-agent-panel/schemas/elicitation';
import { toWorkspaceAgentFilterListOptions } from '~/components/v2/components/workspace/workspace-agent-panel/utils/toWorkspaceAgentFilterListOptions';

export type PrivateWorkspaceAgentPanelSearchDockProps = {
  elicitation: SearchSelectElicitation;
};

/** Pick one keeps only the first pre-pick. */
const getInitialSelectedIndexes = (defaultIndexes: readonly number[] | undefined, multiple: boolean) => {
  const sorted = [...(defaultIndexes ?? [])].sort((left, right) => left - right);

  return multiple ? sorted : sorted.slice(0, 1);
};

export const PrivateWorkspaceAgentPanelSearchDock = memo(
  ({ elicitation }: PrivateWorkspaceAgentPanelSearchDockProps) => {
    const session = usePrivateWorkspaceAgentPanelSession();
    const trackChoice = useTrackWorkspaceAgentFormChoice(elicitation, {
      optionCount: elicitation.payload.options.length,
    });
    const multiple = elicitation.payload.multiple;
    const [query, setQuery] = useState('');
    const [selectedIndexes, setSelectedIndexes] = useState<number[]>(() =>
      getInitialSelectedIndexes(elicitation.payload.defaultIndexes, multiple),
    );

    const handleSelect = useCallback(
      (index: number) => {
        setSelectedIndexes((current) => {
          if (!multiple) {
            return [index];
          }

          return current.includes(index)
            ? current.filter((item) => item !== index)
            : [...current, index].sort((left, right) => left - right);
        });
      },
      [multiple],
    );

    const handleContinue = useCallback(() => {
      if (selectedIndexes.length === 0) {
        return;
      }

      trackChoice({ cta: 'approve', selectedIndexes, usedEscapeHatch: false });
      void session.respondToElicitation({
        elicitationId: elicitation.id,
        response: 'approved',
        selectedIndexes,
      });
    }, [elicitation.id, selectedIndexes, session, trackChoice]);

    const handleCancel = useCallback(() => {
      trackChoice({ cta: 'cancel' });
      void session.respondToElicitation({
        elicitationId: elicitation.id,
        response: 'denied',
      });
    }, [elicitation.id, session, trackChoice]);

    return (
      <WorkspaceAgentPanelConsentDock
        title={<PrivateWorkspaceAgentMonikerText value={elicitation.payload.prompt} />}
        approveLabel="Continue"
        data-testid="WORKSPACE_AGENT_SEARCH"
        isApproveDisabled={selectedIndexes.length === 0}
        onApprove={handleContinue}
        onCancel={handleCancel}
      >
        <WorkspaceAgentFilterList
          multiple={multiple}
          onQueryChange={setQuery}
          onSelect={handleSelect}
          options={toWorkspaceAgentFilterListOptions(elicitation.payload.options)}
          query={query}
          selectedIndexes={selectedIndexes}
        />
      </WorkspaceAgentPanelConsentDock>
    );
  },
);

PrivateWorkspaceAgentPanelSearchDock.displayName = 'PrivateWorkspaceAgentPanelSearchDock';
